import React, { useState, useEffect, useRef } from 'react';
import './RecurringPatternEditor.css';

const RecurringPatternEditor = ({ pattern, startDate, onChange }) => {
  // デフォルト値を明示的に設定
  const [frequency, setFrequency] = useState(pattern?.frequency || 'weekly');
  // 4週間後をデフォルトに設定
  const [endDate, setEndDate] = useState(() => {
    if (pattern?.end_date) return pattern.end_date;
    const fourWeeksLater = new Date();
    fourWeeksLater.setDate(fourWeeksLater.getDate() + 28); // 4週間 = 28日
    return fourWeeksLater.toISOString().split('T')[0];
  });
  // 曜日選択（毎週の場合）- デフォルトは開始日の曜日のみ
  const [selectedDays, setSelectedDays] = useState(() => {
    if (pattern?.days_of_week) return pattern.days_of_week;
    // デフォルトは開始日の曜日のみ（月曜日=0, 日曜日=6）
    const date = startDate ? new Date(startDate) : new Date();
    return [date.getDay() === 0 ? 6 : date.getDay() - 1]; // 日曜日を6に変換
  });

  // onChangeをrefで保持して最新の値を使う
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // パターンが変更されたら親に通知（onChangeを依存配列から除外）
  useEffect(() => {
    const newPattern = {
      frequency,
      interval: 1,
      end_type: 'date',
      end_date: endDate
    };

    // 毎週の場合は曜日を追加
    if (frequency === 'weekly') {
      newPattern.days_of_week = selectedDays;
    }

    onChangeRef.current(newPattern);
  }, [frequency, endDate, selectedDays]);

  return (
    <div className="recurring-pattern-editor">
      <h3 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '12px', color: '#202124' }}>
        繰り返しパターン
      </h3>

      {/* 繰り返し頻度 */}
      <div className="form-group">
        <label>繰り返し</label>
        <select
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #dadce0',
            borderRadius: '4px',
            fontSize: '14px',
            minWidth: '120px'
          }}
        >
          <option value="weekly">毎週</option>
          <option value="monthly">毎月</option>
        </select>
        {frequency === 'weekly' && (
          <div style={{ marginTop: '12px' }}>
            <label id="weekday-selection-label" style={{ fontSize: '13px', color: '#5f6368', marginBottom: '8px', display: 'block' }}>
              繰り返す曜日を選択
            </label>
            <div
              role="group"
              aria-labelledby="weekday-selection-label"
              style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
            >
              {[
                { value: 0, label: '月' },
                { value: 1, label: '火' },
                { value: 2, label: '水' },
                { value: 3, label: '木' },
                { value: 4, label: '金' },
                { value: 5, label: '土' },
                { value: 6, label: '日' }
              ].map(day => (
                <label key={day.value} style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                  <input
                    type="checkbox"
                    checked={selectedDays.includes(day.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedDays([...selectedDays, day.value].sort());
                      } else {
                        setSelectedDays(selectedDays.filter(d => d !== day.value));
                      }
                    }}
                    style={{ marginRight: '4px' }}
                    aria-label={`${day.label}曜日`}
                  />
                  {day.label}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 終了日 */}
      <div className="form-group">
        <label>終了日</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #dadce0',
            borderRadius: '4px',
            fontSize: '14px',
            width: '200px'
          }}
        />
      </div>

      {/* プレビュー */}
      <div style={{
        marginTop: '16px',
        padding: '12px',
        backgroundColor: '#f1f3f4',
        borderRadius: '4px',
        fontSize: '13px',
        color: '#5f6368'
      }}>
        {frequency === 'weekly' && `毎週（${selectedDays.map(d => ['月', '火', '水', '木', '金', '土', '日'][d]).join('・')}）`}
        {frequency === 'monthly' && `毎月`}
        {endDate && `、${endDate}まで`}
      </div>
    </div>
  );
};

export default RecurringPatternEditor;
